<template>
  <div class="date-wrapper">
    <b-btn variant="white"
           class="text-sm border px-4 mb-1"
           id="dash-calendar-input">
        <span v-if="!date.start || !date.end">
          Pick A Date Range..
        </span>
      <span v-else>
          {{ formatDate(date.start) }}
          <span class="icon icon-Arrow_Right text-muted"/>
          {{ formatDate(date.end) }}
        </span>
    </b-btn>

    <div id="boundary">
      <div id="boundary-container" class="pl-5">
        <b-popover target="dash-calendar-input"
                   triggers="click"
                   placement="bottom"
                   container="boundary"
                   class="p-0"
                   ref="popover">
          <flat-pickr v-model="range"
                      @input="onChange"
                      :locale="timezone"
                      :config="calendar.config"/>
          <b-row>
            <b-col>
              <b-button size="sm" variant="light" @click="selectToday" class="text-muted border">Today</b-button>
            </b-col>
            <b-col>
              <b-button size="sm" variant="light" @click="selectThisWeek" class="text-muted border">This Week</b-button>
            </b-col>
          </b-row>
          <div v-if="timezone">
            <cool-label>Timezone</cool-label>
            <b-form-select class="date-picker-timezone-select" @input="timeZoneChange(val)" v-model="timezone"
                           :options="timezoneOptions"></b-form-select>
            <b-form-select class="date-picker-timezone-select" v-model="date.interval" v-if="!hideInterval">
              <option value="MINUTE">Minute</option>
              <option value="HOUR">Hour</option>
              <option value="DAY">Day</option>
              <option value="MONTH">Month</option>
              <option value="NONE">None</option>
            </b-form-select>
          </div>
        </b-popover>
      </div>
    </div>
  </div>
</template>

<script>
  import FlatPickr from 'vue-flatpickr-component'
  import 'flatpickr/dist/flatpickr.css'
  import moment from 'moment-timezone'
  import {timezoneList} from '@/reports/components/ReportUtils'

  export default {
    props: {
      date: {type: Object, required: true},
      hideInterval: { required: false, default: false },
      limitToday: { required: false, default: true }
    },
    created () {
      this.$root.$on('bv::popover::shown', ev => {
        console.log('open')
        this.popover.open = true
      })
      this.$root.$on('bv::popover::hidden', ev => {
        console.log('close')
        this.popover.open = false
      })
      if (!this.date.interval) {
        this.date.interval = 'HOUR'
      }
    },
    data () {
      return {
        popover: {
          open: false,
          changed: true
        },
        timezone: 'UTC',
        range: null,
        calendar: {
          config: {
            dateFormat: 'Y-m-d H:i',
            enableTime: false,
            altInput: true,
            mode: 'range',
            maxDate: this.limitToday ? new Date().fp_incr(1) : null,
            inline: true,
            static: true
          }
        },
        format: 'YYYY-MM-DD',
        //give a full list of utc timezones
        //but only ones with '/' in them, cuz we want the list to be pretty
        timezoneOptions: timezoneList
      }
    },
    mounted () {
      if (this.date.start || this.date.end) {
        this.setRange(this.date.start, this.date.end)
        this.date.start = moment(this.date.start).tz(this.timezone).startOf('day').valueOf()
        this.date.end = moment(this.date.end).tz(this.timezone).endOf('day').valueOf()
      }
    },
    computed: {
      locale () {
        return moment().tz(this.timezone).format('L')
      },
      rangeMillis () {
        let parts = this.range ? this.range.split(' to ') : []
        if (parts.length == 1) {
          /* single date selection */
          parts.push(parts[0])
        }

        if (!parts.length) {
          return {start: null, end: null}
        }

        if (this.date.interval !== 'MINUTE') {
          return {
            start: moment(parts[0]).tz(this.timezone).startOf('day').valueOf(),
            end: moment(parts[1]).tz(this.timezone).endOf('day').valueOf()
          }
        } else {
          return {
            start: moment(parts[0]).tz(this.timezone).valueOf(),
            end: moment(parts[0]).tz(this.timezone).add(1, 'hours').valueOf(),
          }
        }
      },
      timezoneOffsetMillis () {
        return new Date().getTimezoneOffset() * 60 * 1000
      }
    },
    methods: {
      getBoundary () {
        return document.getElementById('boundary')
      },
      parseDate (dateString, format) {
        let timezonedDate = timeZone ? new moment.tz(dateString, format, timeZone)
          : new moment(dateString, format)
        return timezonedDate.toDate()
      },
      formatDate (date) {
        return moment(date).tz(this.timezone).format(this.format)
      },
      timeZoneChange () {
        this.$emit('timeZoneChange')
      },
      onChange () {
        /* compare old values and only re-assign if literal value is different,
          otherwise if we simply always assign them it will trigger watch/change
          updates even if the effective times are the same.. */
        this.popover.changed = true

        let min = this.date.interval === 'MINUTE'
        if (min) {
          this.$set(this.calendar.config, 'enableTime', true)
          this.$set(this.calendar.config, 'mode', 'single')
          this.format = 'YYYY-MM-DD HH'
        } else {
          this.$set(this.calendar.config, 'enableTime', false)
          this.$set(this.calendar.config, 'mode', 'range')
          this.format = 'YYYY-MM-DD'
        }
      }, /*
      formatDate: function (date) {
        return moment(date).tz(this.timezone).format('l')
      }, */
      setRange (startDate, endDate) {
        let start = moment(startDate).tz(this.timezone).format(this.format)
        let end = moment(endDate).tz(this.timezone).format(this.format)
        this.range = start + ' to ' + end
        this.onChange()
      }
      ,
      selectToday () {
        this.setRange(moment().startOf('day').toDate(), moment().endOf('day').toDate())
      }
      ,
      selectThisWeek () {
        this.setRange(moment().startOf('week').toDate(), moment().toDate())
      }
    },
    watch: {
      popover: {
        handler: function () {
          console.log('popover event')
          if (!this.popover.changed) {
            console.log('Popover event with no change')
            return
          } else if (this.popover.open) {
            console.log('Change but popover still open')
          } else {
            console.log('Closed, got change')
            let val = this.rangeMillis
            if (val.start !== this.date.start) {
              this.date.start = val.start
            }

            if (val.end !== this.date.end) {
              this.date.end = val.end
            }
            // this.date.start = this.rangeMillis.start
            this.$emit('change')
            this.popover.changed = false
          }
        },
        deep: true
      },
      timezone () {
        this.onChange()
      },
      interval () {
        this.onChange()
      }
    }
    ,
    components: {
      FlatPickr
    }
  }
</script>

<style scoped>
  .date-picker-timezone-select {
    display: grid;
  }
</style>
<style>
  .popover {
    max-width: 100%;
  }

  .date-wrapper .popover, .date-wrapper .popover-body {
    width: 340px !important;
    min-width: 340px !important;
  }
  .date-wrapper .flatpickr-calendar {
    width: auto !important;
  }
  .flatpickr-calendar {
    box-shadow: none !important;
    --webkit-box-shadow: none !important;
  }

  .flatpickr-dayss, .flatpickr-wrapperr {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }

  @media (max-width: 510px) {

  }
</style>



// WEBPACK FOOTER //
// src/components/DatePicker.vue